'use client';

import React from 'react';
import { cn } from '@/utils';
import { switchVariants } from './Switch.styles';

export interface SwitchOption {
  /**
   * Unique identifier for the option
   */
  value: string;
  /**
   * Display label for the option
   */
  label: string;
}

export interface SwitchProps extends Omit<React.HTMLAttributes<HTMLDivElement>, 'onChange'> {
  /**
   * Array of options to display
   */
  options: SwitchOption[];

  /**
   * Currently selected option value
   */
  value?: string;

  /**
   * Callback when selection changes
   */
  onChange?: (value: string) => void;

  /**
   * Whether the switch is disabled
   */
  disabled?: boolean;

  /**
   * Size variant of the switch
   * @default 'default'
   */
  size?: 'xs' | 'small' | 'default';

  /**
   * ARIA label for the switch group
   */
  ariaLabel?: string;
}

export const Switch: React.FC<SwitchProps> = ({
  options,
  value,
  onChange,
  disabled = false,
  size = 'default',
  className,
  ariaLabel = 'Switch options',
  ...props
}) => {
  const handleOptionClick = (optionValue: string) => {
    if (!disabled && value !== optionValue && onChange) {
      onChange(optionValue);
    }
  };

  const classNames = cn(switchVariants({ size }), className);

  return (
    <div className={classNames} role="group" aria-label={ariaLabel} {...props}>
      {options.map((option) => (
        <button
          key={option.value}
          type="button"
          onClick={() => handleOptionClick(option.value)}
          aria-pressed={value === option.value}
          className={cn(
            'relative box-border flex h-full shrink-0 items-center justify-center gap-4 overflow-clip rounded-full transition-colors',
            size === 'xs' ? 'px-4 py-2' : size === 'small' ? 'px-6 py-3' : 'px-8 py-5',
            value === option.value
              ? 'bg-[var(--color-brand-primary)] text-white'
              : 'text-[var(--color-brand-primary-700)]',
            disabled && 'cursor-not-allowed opacity-50',
          )}
        >
          <span className={cn(
            'whitespace-nowrap text-center font-bold not-italic pointer-events-none',
            size === 'xs' ? 'text-xs leading-4' : size === 'small' ? 'text-sm leading-6' : 'text-[18px] leading-8',
          )}>
            {option.label}
          </span>
        </button>
      ))}
    </div>
  );
};

Switch.displayName = 'Switch';

export default Switch;
